import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PrefetchedDataProps, PrefetchedUserDataProps } from '#src/shared/prefetch';
import { withPrefetchedData } from '#src/shared/prefetch';

import {
	type PetitionDetailsPagePrefetchedContext,
	type PetitionDetailsPagePrefetchedUserContext,
	prefetchPageContext,
	prefetchPageUserContext,
} from '#src/app/pages/petitionDetails/pageContext';
import type { Sharer } from '#src/app/pages/petitionDetails/shared/types';
import { isLoaded } from '#src/app/shared/utils/async';

import { getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';
import {
	buildMetaContext,
	facebookSharerHeadline,
	petitionDetailsPageAlternateUrls,
	petitionDetailsPageCanonical,
	petitionDetailsPageMetadata,
	petitionDetailsPageSchemaOrg,
	petitionDetailsPageTitle,
} from '../shared';
import type { PetitionDetailsMetaContext } from '../shared';

import { getPetitionPsfOgImages } from './api/getPetitionPsfOgImages';

// EXPERIMENT: psf_og_image_experiment — the arms of the experiment. At cleanup, serve the winning image here; the
// routes stay (links already shared keep pointing at them).
// Keep in sync with PSF_OG_IMAGE_VARIATION_NAMES in apps/middle-end/src/gql/shared/petitionShareVisuals/getPsfOgImages.ts,
// which decides the arms that get an /img link. An arm missing here silently serves the /bsv image. An unknown arm
// is not reported, because anyone can request /img/<anything>.
const OG_IMAGE_VARIANTS = ['variant_1', 'variant_2'] as const;
type OgImageVariant = (typeof OG_IMAGE_VARIANTS)[number];

// matches PsfOgImagePhotoBuilder::WIDTH/HEIGHT in change_production (the variant 2 builder subclasses it)
const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

type PrefetchedData = PetitionDetailsPagePrefetchedContext &
	Readonly<{
		sharer?: Sharer;
		armImageUrl?: string;
		metaContext: PetitionDetailsMetaContext;
	}>;

function toOgImageVariant(value: string | undefined): OgImageVariant | undefined {
	return OG_IMAGE_VARIANTS.find((variant) => variant === value);
}

/**
 * The crawler still gets a preview, so these failures are degraded, not broken. They are reported so the experiment's
 * health can be watched in Sentry.
 */
function reportWarning(utilityContext: UtilityContext, error: Error, params: Record<string, string>) {
	void utilityContext.errorReporter.report(
		{ error, context: { feature: 'psf_og_image_experiment' }, params },
		{ severity: 'warning' },
	);
}

async function getArmImageUrl(
	slug: string,
	variant: OgImageVariant,
	utilityContext: UtilityContext,
): Promise<string | undefined> {
	try {
		const result = await getPetitionPsfOgImages(slug, utilityContext);
		// a link to a deleted petition is not an experiment problem
		if (!result.petitionFound) return undefined;
		// Not reported: null is usually normal (the photo was removed or replaced and the new images are still
		// rendering, or the old images were deleted), and old links keep getting crawled. A failed monorail call
		// is already reported by the middle-end's fetchShareVisualsByTag.
		if (result.images) return variant === 'variant_1' ? result.images.variant1.url : result.images.variant2.url;
	} catch (error) {
		reportWarning(utilityContext, new Error('PSF OG image fetch failed on the crawler route', { cause: error }), {
			slug,
			variant,
		});
	}
	return undefined;
}

async function getSharerOrReport(
	slug: string,
	sharerUserId: string,
	utilityContext: UtilityContext,
): Promise<Sharer | undefined> {
	try {
		return await getSharer(sharerUserId, utilityContext);
	} catch (error) {
		// the sharer title is part of what the /fbog arm links show
		reportWarning(utilityContext, new Error('Sharer fetch failed on the OG image crawler route', { cause: error }), {
			slug,
			sharerUserId,
		});
		return undefined;
	}
}

/**
 * Crawler route for the `/img/:variant` share link segment. Serves the arm image as og:image. For an unknown arm,
 * a petition without arm images or a failed fetch, it serves today's `/bsv` image instead, never an error page:
 * channels cache the first preview they get.
 */
export const PetitionDetailsOgImage = withPrefetchedData(
	function PetitionDetailsOgImage({
		prefetchedData,
		prefetchedUserData,
	}: PrefetchedDataProps<PrefetchedData> & PrefetchedUserDataProps<PetitionDetailsPagePrefetchedUserContext>) {
		return <CommonPetitionDetails prefetchedData={prefetchedData} prefetchedUserData={prefetchedUserData} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchData: async (context) => {
			const {
				params: { slug: slugParam, sharerUserId, variant: variantParam },
			} = context;
			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			const slug = slugParam!;
			const variant = toOgImageVariant(variantParam);
			const [pageContext, armImageUrl, sharer] = await Promise.all([
				prefetchPageContext(slug, context),
				variant ? getArmImageUrl(slug, variant, context.utilityContext) : undefined,
				sharerUserId ? getSharerOrReport(slug, sharerUserId, context.utilityContext) : undefined,
			]);
			return {
				...pageContext,
				sharer,
				armImageUrl,
				metaContext: buildMetaContext(context),
			};
		},
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		prefetchUserData: async (context) => prefetchPageUserContext(context.params.slug!, context),
		pageTitle: (ctx) => petitionDetailsPageTitle(ctx),
		pageCanonical: (ctx) => petitionDetailsPageCanonical(ctx),
		pageMetadata: ({ prefetchedData, i18n, getStaticAssetUrl }) => {
			if (!isLoaded(prefetchedData.pageData)) {
				return undefined;
			}
			const { armImageUrl } = prefetchedData;
			return petitionDetailsPageMetadata({
				prefetchedData,
				i18n,
				getStaticAssetUrl,
				metaContext: prefetchedData.metaContext,
				// the arm image replaces the /bsv one on these links, so the fallback is the /bsv image
				bestShareVisual: true,
				shareHeadlineOverrides: {
					...facebookSharerHeadline({
						pageData: prefetchedData.pageData.pageData,
						sharer: prefetchedData.sharer,
						i18n,
					}),
					...(armImageUrl && { imageUrl: armImageUrl, imageWidth: OG_IMAGE_WIDTH, imageHeight: OG_IMAGE_HEIGHT }),
				},
			});
		},
		pageSchemaOrg: (ctx) =>
			petitionDetailsPageSchemaOrg({
				prefetchedData: ctx.prefetchedData,
				resolveUrl: ctx.resolveUrl,
				defineArticle: ctx.defineArticle,
				defineBreadcrumb: ctx.defineBreadcrumb,
				definePerson: ctx.definePerson,
			}),
		pageAlternateUrls: (ctx) => petitionDetailsPageAlternateUrls(ctx),
	},
);
